"use client";

import { useAtom } from "jotai";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { registrationFormAtom, RegistrationFormData } from "~/atoms";
import AddressField, { Address } from "~/components/address/AddressField";
import Button from "~/components/buttons/Button";
import Flex from "~/components/flex/Flex";
import { Icon, IconName } from "~/components/icons/Icon";
import Dropdown from "~/components/inputs/Dropdown";
import { NumberInput } from "~/components/inputs/NumberInput";
import PhoneInput from "~/components/inputs/PhoneInput";
import { FormItemTypeEnum, TextInput } from "~/components/inputs/TextInput";
import Modal from "~/components/modal/Modal";
import TermsAndConditionsAgreement from "~/components/modal/TermsAndConditionsAgreement";
import Text from "~/components/texts/Text";
import { SEARCH_PARAM_REGISTRATION_MODAL } from "~/constants";
import { EXTERNAL_ROUTES_MAP_ENUM, ROUTES_MAP } from "~/constants/routes";
import { useCurrentLocale, useI18n } from "~/locales/client";
import { ButtonTypesEnum } from "~/types-and-enums/generalTypes";
import { ColorModeEnum } from "~/utils/generalUtils";
import { colors, spacing } from "~/utils/tailwindUtils";
import { usePostHog } from "~/hooks/usePostHog";

/**
 * Registration Modal Component
 *
 * Displays a 2-step registration form for supplier registration.
 */
const RegistrationModal = () => {
  const searchParams = useSearchParams();
  const translate = useI18n();
  const locale = useCurrentLocale();
  const { captureEvent } = usePostHog();
  const [formData, setFormData] = useAtom(registrationFormAtom);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof RegistrationFormData, string>>>({});
  const [address, setAddress] = useState<Address | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const router = useRouter();

  // Check if modal should be open
  const shouldOpen = searchParams.get(SEARCH_PARAM_REGISTRATION_MODAL) === "true";

  // Business types with translated labels
  const BUSINESS_TYPES = useMemo(
    () => [
      { value: "DISTRIBUTOR", label: translate("general.business-type.distributor") },
      { value: "MANUFACTURER", label: translate("general.business-type.manufacturer") },
      { value: "OTHER", label: translate("general.business-type.other") },
    ],
    [translate]
  );

  // Initialize address from form data when modal opens
  useEffect(() => {
    if (shouldOpen && !address && (formData.street || formData.city || formData.postal_code || formData.country)) {
      setAddress({
        street: formData.street,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country,
        state: null,
        state_code: null,
        country_code: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpen]); // Only when modal opens

  // Update form data when address changes
  useEffect(() => {
    if (address) {
      setFormData((prev) => ({
        ...prev,
        street: address.street,
        city: address.city,
        postal_code: address.postal_code,
        country: address.country || "",
      }));
    }
  }, [address, setFormData]);

  const handleClose = (): void => {
    window.location.href = `${EXTERNAL_ROUTES_MAP_ENUM.andercore}/${locale}`;
  };

  const validateStep1 = (): boolean => {
    const newErrors: Partial<Record<keyof RegistrationFormData, string>> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = translate("general.first-name-required");
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = translate("general.last-name-required");
    }
    if (!formData.email_address.trim()) {
      newErrors.email_address = translate("general.email-required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
      newErrors.email_address = translate("general.invalid-email");
    }
    if (!formData.telephone_number.trim()) {
      newErrors.telephone_number = translate("general.telephone-number-required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Partial<Record<keyof RegistrationFormData, string>> = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = translate("general.company-name-required");
    }
    if (!formData.business_type) {
      newErrors.business_type = translate("general.business-type-required");
    }
    const missingAddressFields: string[] = [];
    if (!formData.street?.trim()) {
      missingAddressFields.push(translate("general.street-house-number"));
    }
    if (!formData.city?.trim()) {
      missingAddressFields.push(translate("general.city"));
    }
    if (!formData.country?.trim()) {
      missingAddressFields.push(translate("general.country"));
    }
    if (!formData.postal_code?.trim()) {
      missingAddressFields.push(translate("general.zip-code"));
    }

    if (missingAddressFields.length > 0) {
      const fieldsText =
        missingAddressFields.length === 1
          ? missingAddressFields[0]
          : `${missingAddressFields.slice(0, -1).join(", ")} ${translate("general.and")} ${
              missingAddressFields[missingAddressFields.length - 1]
            }`;
      newErrors.street =
        missingAddressFields.length === 1
          ? translate("general.missing-field", { field: fieldsText })
          : translate("general.missing-fields", { fields: fieldsText });
    }
    if (!formData.annual_revenue_in_net_eur.trim()) {
      newErrors.annual_revenue_in_net_eur = translate("general.annual-revenue-required");
    }
    if (!termsAccepted) {
      setError(translate("general.terms-required"));
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
        setError(null);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateStep2()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email_address: formData.email_address,
        company_name: formData.company_name,
        telephone_number: formData.telephone_number,
        business_type: formData.business_type,
        street: formData.street,
        city: formData.city,
        country: formData.country,
        postal_code: formData.postal_code,
        annual_revenue_in_net_eur: formData.annual_revenue_in_net_eur,
      };

      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        captureEvent("user_registration_completed", {
          business_type: payload.business_type,
          locale,
        });
      } else {
        captureEvent("user_registration_failed", { status: response.status, locale });
      }
    } catch (err: unknown) {
      console.error("Registration error:", err);
      captureEvent("user_registration_failed", { error: String(err), locale });
    } finally {
      router.replace(`/${locale}${ROUTES_MAP.authRegisterSuccess}`, { scroll: false });
    }
  };

  const updateField = (field: keyof RegistrationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  if (!shouldOpen) return null;

  return (
    <Modal isOpen={shouldOpen} closeModal={handleClose} classname="pt-0 pb-6 md:pt-0 lg:pt-0 lg:pb-10" size="lg">
      <Flex className="flex-col gap-6 w-full max-w-xl mx-auto">
        {/* Progress Indicator */}
        <Flex className="items-center justify-center gap-1 md:gap-2">
          {/* Step 1 */}
          <Flex className="items-center gap-2">
            <Flex
              className={twMerge(
                "w-6 h-6 md:w-8 md:h-8 rounded-full items-center justify-center",
                currentStep >= 1 ? "bg-gray-800" : "bg-gray-300"
              )}
            >
              {currentStep > 1 ? (
                <Icon name={IconName.CheckCircle} size={spacing[4]} color={colors.white} />
              ) : (
                <Text className={twMerge("font-semibold", currentStep >= 1 ? "text-white" : "text-gray-600")}>1</Text>
              )}
            </Flex>
            <Text className="font-medium text-gray-700">{translate("general.step-create")}</Text>
          </Flex>
          <div className={twMerge("w-12 h-0.5", currentStep >= 2 ? "bg-gray-800" : "bg-gray-300")} />
          {/* Step 2 */}
          <Flex className="items-center gap-2">
            <Flex
              className={twMerge(
                "w-6 h-6 md:w-8 md:h-8 rounded-full items-center justify-center",
                currentStep >= 2 ? "bg-gray-800" : "bg-gray-300"
              )}
            >
              {currentStep > 2 ? (
                <Icon name={IconName.CheckCircle} size={spacing[4]} color={colors.white} />
              ) : (
                <Text className={twMerge("font-semibold", currentStep >= 2 ? "text-white" : "text-gray-600")}>2</Text>
              )}
            </Flex>
            <Text className="font-medium text-gray-700">{translate("general.step-configure")}</Text>
          </Flex>
        </Flex>

        {/* Title */}
        <Text className="text-xl md:text-2xl font-bold text-gray-900">
          {currentStep === 1 ? translate("general.create-user-account") : translate("general.configure-details")}
        </Text>

        {/* Error Message */}
        {error && (
          <Flex className="bg-red-50 border border-red-200 rounded-lg p-3">
            <Text className="text-sm text-red-700">{error}</Text>
          </Flex>
        )}

        {/* Form */}
        <form onSubmit={currentStep === 2 ? handleSubmit : (e) => e.preventDefault()} className="flex flex-col gap-6">
          {currentStep === 1 ? (
            <>
                {/* Step 1 Fields */}
                <Flex className="flex-col md:flex-row gap-4">
                  <Flex className="flex-col gap-2 flex-1">
                    <label htmlFor="first_name" className="text-sm font-medium text-gray-700">
                      {translate("general.first-name")} *
                    </label>
                    <TextInput
                      name="first_name"
                      type={FormItemTypeEnum.Text}
                      value={formData.first_name}
                      onChange={(e) => updateField("first_name", e.target.value)}
                      placeholder="John"
                      classNames={twMerge("bg-gray-50 border-gray-300", errors.first_name && "border-red-500")}
                      autoComplete="given-name"
                    />
                    {errors.first_name && <Text className="text-xs text-red-600">{errors.first_name}</Text>}
                  </Flex>

                  <Flex className="flex-col gap-2 flex-1">
                    <label htmlFor="last_name" className="text-sm font-medium text-gray-700">
                      {translate("general.last-name")} *
                    </label>
                    <TextInput
                      name="last_name"
                      type={FormItemTypeEnum.Text}
                      value={formData.last_name}
                      onChange={(e) => updateField("last_name", e.target.value)}
                      placeholder="Doe"
                      classNames={twMerge("bg-gray-50 border-gray-300", errors.last_name && "border-red-500")}
                      autoComplete="family-name"
                    />
                    {errors.last_name && <Text className="text-xs text-red-600">{errors.last_name}</Text>}
                  </Flex>
                </Flex>

                <Flex className="flex-col gap-2">
                  <label htmlFor="email_address" className="text-sm font-medium text-gray-700">
                    {translate("general.email-address")} *
                  </label>
                  <TextInput
                    name="email_address"
                    type={FormItemTypeEnum.Text}
                    value={formData.email_address}
                    onChange={(e) => updateField("email_address", e.target.value)}
                    placeholder="jane.doe@company.com"
                    classNames={twMerge("bg-gray-50 border-gray-300", errors.email_address && "border-red-500")}
                    autoComplete="email"
                  />
                  {errors.email_address && <Text className="text-xs text-red-600">{errors.email_address}</Text>}
                </Flex>

                <Flex className="flex-col gap-2">
                  <label htmlFor="telephone_number" className="text-sm font-medium text-gray-700">
                    {translate("general.telephone-number")} *
                  </label>
                  <PhoneInput
                    name="telephone_number"
                    value={formData.telephone_number}
                    onChange={(value) => updateField("telephone_number", value)}
                    placeholder="176 0000 0000"
                    classNames={twMerge(errors.telephone_number && "border-red-500")}
                  />
                  {errors.telephone_number && <Text className="text-xs text-red-600">{errors.telephone_number}</Text>}
                </Flex>

                {/* Continue Button */}
                <Button
                  type="button"
                  buttonStyle={ButtonTypesEnum.primary}
                  text={translate("general.continue")}
                  onClick={handleNext}
                  buttonClassName="bg-red-500 hover:bg-red-600 rounded-lg py-2.5"
                />
            </>
          ) : (
            <>
                {/* Step 2 Fields */}
                <Flex className="flex-col gap-2">
                  <label htmlFor="company_name" className="text-sm font-medium text-gray-700">
                    {translate("general.company-name")} *
                  </label>
                  <TextInput
                    name="company_name"
                    type={FormItemTypeEnum.Text}
                    value={formData.company_name}
                    onChange={(e) => updateField("company_name", e.target.value)}
                    placeholder={translate("general.enter-legal-entity-name")}
                    classNames={twMerge("bg-gray-50 border-gray-300", errors.company_name && "border-red-500")}
                    autoComplete="organization"
                  />
                  {errors.company_name && <Text className="text-xs text-red-600">{errors.company_name}</Text>}
                </Flex>

                <Flex className="flex-col md:flex-row gap-4 w-full">
                  <Flex className="flex-col gap-2 flex-1 min-w-0">
                    <label htmlFor="business_type" className="text-sm font-medium text-gray-700">
                      {translate("general.type-of-business")} *
                    </label>
                    <Dropdown
                      name="business_type"
                      options={BUSINESS_TYPES}
                      value={formData.business_type}
                      onChange={(value) => updateField("business_type", value)}
                      placeholder={translate("general.select")}
                      error={!!errors.business_type}
                      useRelativePositioning={true}
                    />
                    {errors.business_type && <Text className="text-xs text-red-600">{errors.business_type}</Text>}
                  </Flex>

                  <Flex className="flex-col gap-2 flex-1 min-w-0">
                    <label htmlFor="annual_revenue_in_net_eur" className="text-sm font-medium text-gray-700">
                      {translate("general.annual-revenue")} *
                    </label>
                    <NumberInput
                      name="annual_revenue_in_net_eur"
                      value={formData.annual_revenue_in_net_eur}
                      onChange={(e) => updateField("annual_revenue_in_net_eur", e.target.value)}
                      placeholder="e.g. 5.000.000"
                      classNames={twMerge(
                        "bg-gray-50 border-gray-300 w-full",
                        errors.annual_revenue_in_net_eur && "border-red-500"
                      )}
                    />
                    {errors.annual_revenue_in_net_eur && (
                      <Text className="text-xs text-red-600">{errors.annual_revenue_in_net_eur}</Text>
                    )}
                  </Flex>
                </Flex>

                <Flex className="flex-col gap-2">
                  <label htmlFor="address" className="text-sm font-medium text-gray-700">
                    {translate("general.full-address")} *
                  </label>
                  <AddressField
                    value={address || undefined}
                    onChange={(addr) => {
                      setAddress(addr);
                      updateField("street", addr.street);
                      updateField("city", addr.city);
                      updateField("postal_code", addr.postal_code);
                      updateField("country", addr.country || "");
                    }}
                    placeholder={translate("general.start-typing-address")}
                    error={!!errors.street}
                    useRelativePositioning={true}
                  />
                  {errors.street && <Text className="text-xs text-red-600">{errors.street}</Text>}
                </Flex>

                {/* Terms and Conditions */}
                <Flex className="flex-col gap-2">
                  <Flex
                    className="items-start gap-2"
                    onClick={() => {
                      setTermsAccepted(!termsAccepted);
                      setError(null);
                    }}
                  >
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={termsAccepted}
                      aria-labelledby="terms-label"
                      className="mt-1 rounded transition-all"
                    >
                      <Icon
                        name={termsAccepted ? IconName.CheckboxChecked : IconName.CheckboxUnchecked}
                        size={spacing[4]}
                        color={termsAccepted ? colors.red[500] : colors.gray[500]}
                        colorMode={termsAccepted ? ColorModeEnum.Fill : ColorModeEnum.Stroke}
                        className="cursor-pointer"
                      />
                    </button>
                    <label id="terms-label" htmlFor="terms" className="text-sm text-gray-700 cursor-pointer">
                      <TermsAndConditionsAgreement />
                    </label>
                  </Flex>
                </Flex>

                {/* Action Buttons */}
                <Flex className="gap-4 justify-between">
                  <Button
                    type="button"
                    buttonStyle={ButtonTypesEnum.default}
                    text={translate("general.back")}
                    onClick={handleBack}
                    buttonClassName="rounded-lg border border-gray-200 hover:border-gray-300 py-2.5 px-6 max-w-fit"
                  />
                  <Button
                    type="submit"
                    buttonStyle={ButtonTypesEnum.primary}
                    text={translate("general.complete-registration")}
                    isLoading={loading}
                    disabled={loading || !termsAccepted}
                    buttonClassName="bg-red-500 hover:bg-red-600 rounded-lg py-2.5 max-w-1/2"
                  />
                </Flex>
            </>
          )}
        </form>
      </Flex>
    </Modal>
  );
};

export default RegistrationModal;
